import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import NotificationService from '../services/notification.service';
import { getStoredConsent } from '../services/consentService';

const NotificationContext = createContext(null);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState(NotificationService.getPermissionState());
  const [isSupported] = useState(NotificationService.isWebPushSupported());
  const [showSoftPrompt, setShowSoftPrompt] = useState(false);

  const userId = currentUser?.id || currentUser?.userId || currentUser?.user_id;

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!userId) return;
    try {
      const count = await NotificationService.getUnreadCount();
      setUnreadCount(count);
    } catch (err) {
      console.error('[NotificationContext] fetchUnreadCount error:', err);
    }
  }, [userId]);

  // Fetch recent notifications
  const fetchNotifications = useCallback(async (page = 1, limit = 20) => {
    if (!userId) return;
    try {
      setLoading(true);
      const data = await NotificationService.getNotifications({ page, limit });
      if (data && data.notifications) {
        setNotifications(data.notifications);
      }
    } catch (err) {
      console.error('[NotificationContext] fetchNotifications error:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Mark single notification as read & track interaction
  const markAsRead = useCallback(async (notificationId, broadcastId = null) => {
    try {
      await Promise.allSettled([
        NotificationService.markAsRead(notificationId),
        NotificationService.trackNotificationOpen(notificationId, broadcastId)
      ]);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('[NotificationContext] markAsRead error:', err);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await NotificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('[NotificationContext] markAllAsRead error:', err);
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await NotificationService.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      fetchUnreadCount();
    } catch (err) {
      console.error('[NotificationContext] deleteNotification error:', err);
    }
  }, [fetchUnreadCount]);

  // Enable Web Push Subscription
  const enableWebPush = useCallback(async () => {
    try {
      const result = await NotificationService.subscribeToWebPush();
      setPermission(NotificationService.getPermissionState());
      setShowSoftPrompt(false);
      localStorage.setItem('ks_push_enabled', 'true');
      return result;
    } catch (err) {
      console.error('[NotificationContext] enableWebPush error:', err);
      setShowSoftPrompt(false);
      throw err;
    }
  }, []);

  // Dismiss Soft Prompt
  const dismissSoftPrompt = useCallback(() => {
    setShowSoftPrompt(false);
    localStorage.setItem('ks_push_prompt_dismissed', Date.now().toString());
  }, []);

  // Auto-sync notifications and decide on Soft Prompt trigger
  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      setUnreadCount(0);
      setShowSoftPrompt(false);
      return;
    }

    fetchUnreadCount();
    fetchNotifications();

    // Check if we should show the soft primer prompt
    if (isSupported) {
      const currentPerm = NotificationService.getPermissionState();
      setPermission(currentPerm);

      if (currentPerm === 'default') {
        const lastDismissed = localStorage.getItem('ks_push_prompt_dismissed');
        const fourteenDays = 14 * 24 * 60 * 60 * 1000;
        const shouldShow = !lastDismissed || (Date.now() - parseInt(lastDismissed, 10) > fourteenDays);

        if (shouldShow) {
          const schedulePrompt = () => {
            return setTimeout(() => {
              setShowSoftPrompt(true);
            }, 1800);
          };

          if (getStoredConsent()) {
            const timer = schedulePrompt();
            return () => clearTimeout(timer);
          } else {
            let activeTimer = null;
            const onConsentSaved = () => {
              activeTimer = schedulePrompt();
            };
            window.addEventListener('ks:consent-updated', onConsentSaved, { once: true });
            return () => {
              window.removeEventListener('ks:consent-updated', onConsentSaved);
              if (activeTimer) clearTimeout(activeTimer);
            };
          }
        }
      } else if (currentPerm === 'granted') {
        // If already granted, ensure token is registered with backend
        NotificationService.subscribeToWebPush().catch(() => {});
      }
    }
  }, [userId, isSupported, fetchUnreadCount, fetchNotifications]);

  const value = {
    notifications,
    unreadCount,
    loading,
    permission,
    isSupported,
    showSoftPrompt,
    setShowSoftPrompt,
    dismissSoftPrompt,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    enableWebPush
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
